import type { Namespace, TFunction } from "i18next";
import type { LinkProps } from "@tanstack/react-router";
import {
  BookMarked,
  BookOpen,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  Home,
  KeyRound,
  LayoutGrid,
  ListChecks,
  RefreshCw,
  Route,
  Trophy,
  Users,
  Wrench,
} from "lucide-react";

/**
 * Above the groups and in none of them: it points at all three. The words live in the `navigatie`
 * catalog under the same key; this file keeps only the structure.
 */
export const overzicht = {
  to: "/",
  key: "overzicht",
  icon: Home,
} as const;

/**
 * Pinned to the top so a beheerder who does not know what to do need not guess which group holds
 * the answer. Only its position is special; it is reached like any grouped page.
 */
export const handleiding = {
  to: "/handleiding",
  key: "handleiding",
  icon: BookMarked,
} as const;

/**
 * For `navigation.test.ts`: which paths sit outside the groups is one fact. The sidebar and
 * breadcrumb still branch by name, because Overzicht's label lives under its own catalog key.
 */
export const boven = [overzicht, handleiding] as const;

/**
 * Inschrijvingen is namens handelen, Seizoen is everything carrying a `?seizoen=`, and Beheer is
 * what outlives a seizoen (ADR-0028). Here rather than in AppSidebar because three screens read
 * this same tree, and copies would drift the first time a page is renamed.
 */
export const groups = [
  {
    key: "inschrijvingen",
    items: [
      { to: "/ploegen", key: "ploegen", icon: ClipboardList },
      { to: "/ondersteuners", key: "ondersteuners", icon: Users },
    ],
  },
  {
    key: "seizoen",
    items: [
      { to: "/seizoenen", key: "seizoenen", icon: Trophy },
      { to: "/wedstrijddagen", key: "wedstrijddagen", icon: CalendarDays },
      {
        to: "/inschrijfperiodes",
        key: "inschrijfperiodes",
        icon: CalendarClock,
      },
      {
        to: "/reistijden",
        key: "reistijden",
        icon: Route,
        // Two lists behind one entry, for the reason stamgegevens has four: they are consulted
        // on different occasions - korps reistijden when a meldadres moves, ondersteuner
        // reistijden when a postcode is missing - and stacking them buries the one you came for
        // (ADR-0053). The parent is their voordeur, exactly as /stamgegevens is.
        items: [
          { to: "/reistijden/korpsen", key: "reistijdenKorpsen" },
          { to: "/reistijden/ondersteuners", key: "reistijdenOndersteuners" },
        ],
      },
      { to: "/planning", key: "planning", icon: LayoutGrid },

      // After the planning, because that is the order the season is built in: the boekje prints
      // a planning, and there is nothing to print before one exists.
      {
        to: "/wedstrijdboekjes",
        key: "wedstrijdboekjes",
        icon: BookOpen,
        // The template is global, not one seizoen's - ADR-0071 - so it is its own page rather
        // than a card wedged into a seizoen-scoped list.
        items: [
          { to: "/wedstrijdboekjes/sjabloon", key: "wedstrijdboekjesSjabloon" },
        ],
      },
    ],
  },
  {
    key: "beheer",
    items: [
      {
        to: "/stamgegevens",
        key: "stamgegevens",
        icon: Wrench,
        // The four lists behind one entry. They are edited on different occasions - functies
        // when JBNL changes the jurering, klassen and niveaus almost never, speelschema's when
        // a grid shifts for a new season - so they are pages rather than four cards stacked on
        // one, and the parent is their overview. Only the sidebar and the breadcrumb draw these;
        // the Overzicht shows the parent and lets that page be their voordeur.
        items: [
          { to: "/stamgegevens/functies", key: "functies" },
          { to: "/stamgegevens/klassen", key: "klassen" },
          { to: "/stamgegevens/niveaus", key: "niveaus" },
          { to: "/stamgegevens/speelschemas", key: "speelschemas" },
          // Curates the wedstrijddag form's quick-select phrases (#177); outlives a seizoen
          // like the other four, so it is a fifth list here rather than a Beheer entry of its own.
          { to: "/stamgegevens/standaardteksten", key: "standaardteksten" },
        ],
      },
      { to: "/bevoegdheden", key: "bevoegdheden", icon: ListChecks },
      // The exception path, reached when somebody cannot get in rather than in the course of
      // running a seizoen. Koppelingen was a second entry here over the same table; it is this
      // screen's `wacht` filter now.
      { to: "/lokale-accounts", key: "lokaleAccounts", icon: KeyRound },
      // After it, and not about a person at all: where a beheerder goes when JBNL Portaal or VBS
      // has been corrected and the tool should not wait until tonight to know it (ADR-0067).
      { to: "/synchronisaties", key: "synchronisaties", icon: RefreshCw },
    ],
  },
] as const;

/**
 * Generic over the caller's namespaces, since the three screens hold different sets. The cast is
 * what a computed key costs: a missing entry leaves the key on screen, which is loud enough.
 */
type Vertaler = (key: string) => string;

const vertaal = <N extends Namespace>(t: TFunction<N>, key: string) =>
  (t as unknown as Vertaler)(key);

export const paginaLabel = <N extends Namespace>(
  t: TFunction<N>,
  key: string,
) => vertaal(t, `navigatie:paginas.${key}.label`);

/** The one-line description under a card on the Overzicht. Only the eleven pages carry one. */
export const paginaOmschrijving = <N extends Namespace>(
  t: TFunction<N>,
  key: string,
) => vertaal(t, `navigatie:paginas.${key}.omschrijving`);

export const groepLabel = <N extends Namespace>(t: TFunction<N>, key: string) =>
  vertaal(t, `navigatie:groepen.${key}`);

/** The whole tree, for the screens that want every part of it. */
export const navigation = { overzicht, handleiding, boven, groups } as const;

/** `to` is absent for the group headings: they organise the menu but are not pages. */
export type Kruimel = { label: string; to?: LinkProps["to"] };

/** The breadcrumb for one grouped item, or `undefined` when `pathname` is neither it nor one of its sub-pages. */
function kruimelVoorItem<N extends Namespace>(
  t: TFunction<N>,
  group: (typeof groups)[number],
  item: (typeof groups)[number]["items"][number],
  pathname: string,
): Kruimel[] | undefined {
  if (pathname === item.to) {
    return [
      { label: groepLabel(t, group.key) },
      { label: paginaLabel(t, item.key) },
    ];
  }

  if ("items" in item) {
    const sub = item.items.find((s) => s.to === pathname);
    if (sub) {
      return [
        { label: groepLabel(t, group.key) },
        { label: paginaLabel(t, item.key), to: item.to },
        { label: paginaLabel(t, sub.key) },
      ];
    }
  }

  return undefined;
}

/**
 * Empty for a path the menu does not know, so the header draws nothing rather than a half-trail.
 * Both `boven` pages are one crumb and no group: a heading over either would be invented.
 */
export function kruimelpad<N extends Namespace>(
  t: TFunction<N>,
  pathname: string,
): Kruimel[] {
  if (pathname === overzicht.to) {
    return [{ label: vertaal(t, "navigatie:overzicht.label") }];
  }

  if (pathname === handleiding.to) {
    return [{ label: paginaLabel(t, handleiding.key) }];
  }

  for (const group of groups) {
    for (const item of group.items) {
      const kruimel = kruimelVoorItem(t, group, item, pathname);
      if (kruimel) return kruimel;
    }
  }

  return [];
}
